import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BEHAVIOR_TRAIT_CHIP_BY_ID, BEHAVIOR_TRAIT_GROUPS, COLLAPSED_SLOTS } from '../../data/saenggibu/behaviorTraitChips';
import { scenesForChip } from '../../data/saenggibu/behaviorTraitScenes';
import {
  buildCollapsedChips,
  chipUsageKey,
  isTraitSelected,
  parsePromptPieces,
  readChipUsage,
  recordChipUsage,
  searchBehaviorTraits,
  toggleTrait,
} from '../../utils/behaviorTraitSelection';
import './BehaviorTraitPicker.css';

const CheckIcon = () => (
  <svg className="saenggibu-trait__check" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
    <path d="M2.5 6.2l2.3 2.3 4.7-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
    <circle cx="7" cy="7" r="4.6" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <path d="M10.4 10.4L14 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

// 한글 입력 중 Enter 는 글자 확정용이라 무시한다(마지막 글자가 빠지거나 두 번 저장되는 것을 막는다).
const isComposingKey = (event) => event.nativeEvent?.isComposing || event.keyCode === 229;

// '보인 모습' 줄을 × 로 이만큼 닫은 교사에게는 더 이상 저절로 열지 않는다(찾기로는 계속 찾을 수 있다).
// 교무실 공용 PC 처럼 한 브라우저를 여러 교사가 쓰므로 교사마다 따로 센다.
const sceneDismissKey = (scope) => `wq_behavior_scene_dismiss_v1:${scope || 'anon'}`;
const SCENE_AUTO_OPEN_LIMIT = 5;

const readSceneDismissCount = (scope) => {
  try {
    return Number(window.localStorage.getItem(sceneDismissKey(scope))) || 0;
  } catch (error) {
    return 0;
  }
};

const writeSceneDismissCount = (scope, count) => {
  try {
    window.localStorage.setItem(sceneDismissKey(scope), String(count));
  } catch (error) {
    // 저장이 막힌 브라우저에서는 이번 화면에서만 센다.
  }
};

// 선생님들이 익숙한 색 구분: 잘하는 점 파랑, 도와줄 점 노랑, 내 칩 회색.
// 장면은 고르기 전엔 흰색, 고르면 부모 특성 색으로 채운다.
const chipTone = (item) => {
  if (item.type === 'scene') {
    return BEHAVIOR_TRAIT_CHIP_BY_ID[item.parentId]?.type === 'negative' ? 'scene saenggibu-trait__chip--scene-negative' : 'scene';
  }
  if (item.isCustom) return 'custom';
  return item.type === 'negative' ? 'negative' : 'positive';
};

// 특성을 빼면 그 특성의 장면 줄에서 넣은 장면도 같이 뺀다(장면 줄이 닫혀 다시 끌 수 없으므로).
// 찾기로 넣었거나 직접 쓴 같은 문장은 rowSceneTexts 에 없으니 남는다.
const removeTraitWithScenes = (prompt, item, rowSceneTexts) => scenesForChip(item.id)
  .filter((scene) => rowSceneTexts.has(scene.text))
  .reduce((acc, scene) => (isTraitSelected(acc, scene.text) ? toggleTrait(acc, scene.text) : acc), toggleTrait(prompt, item.text));

const normalizeCustoms = (list) => (Array.isArray(list) ? list : [])
  .map((item) => (typeof item === 'string' ? { text: item } : item))
  .filter((item) => item && String(item.text || '').trim())
  .map((item) => ({ text: String(item.text).trim(), type: 'positive', isCustom: true }));

/**
 * 행동발달 '학생 특성' 칩 고르기.
 *
 * 첫 화면 예산: 그룹마다 한 줄(칩 4개 안팎 + 더보기), 맨 위에 '특성 찾기' 하나, 고르기 전에는 회색 한 가지.
 * 고른 상태는 prompt 글자에서 매번 계산하고, 칩을 다시 누르면 그 조각만 뺀다.
 * 자주 쓴 칩 순서는 페이지를 열 때 한 번만 읽어서, 쓰는 도중에는 칩 자리가 바뀌지 않는다.
 * 모바일은 줄마다 옆으로 밀어 보고, 더보기·찾기는 바텀시트로 연다.
 */
const BehaviorTraitPicker = ({
  prompt = '',
  onPromptChange,
  isMobile = false,
  customExamples = null,
  onSaveCustom,
  onDeleteCustom,
  isAnonymousUser = false,
  onRequireLogin,
  usageScope = 'anon',
  onTrack,
}) => {
  const baseId = useId();
  const [usage, setUsage] = useState(() => readChipUsage(usageScope));
  const [openGroup, setOpenGroup] = useState(null);
  const [addingGroup, setAddingGroup] = useState(null);
  const [newChipText, setNewChipText] = useState('');
  const [savingCustom, setSavingCustom] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pickingSaveGroup, setPickingSaveGroup] = useState(false);
  const [sheetTab, setSheetTab] = useState(null);
  const [sceneFor, setSceneFor] = useState(null);
  const [sceneDismissCount, setSceneDismissCount] = useState(() => readSceneDismissCount(usageScope));
  const searchInputRef = useRef(null);
  const sheetOpenerRef = useRef(null);
  const lastSearchRef = useRef({ query: '', found: 0 });
  const rowSceneTextsRef = useRef(new Map());

  useEffect(() => {
    setUsage(readChipUsage(usageScope));
    setSceneDismissCount(readSceneDismissCount(usageScope));
  }, [usageScope]);

  const pieceSet = useMemo(() => new Set(parsePromptPieces(prompt)), [prompt]);
  const isSelected = (text) => pieceSet.has(text) || isTraitSelected(prompt, text);

  // 장면 줄에서 넣은 장면(장면 글자 → 부모 특성 글자). 입력칸에서 장면이나 부모 특성이 빠지면
  // (다음 학생, 직접 지움) 잊어서, 남은 문장은 직접 쓴 글처럼 특성을 빼도 남는다.
  useEffect(() => {
    const rowSceneTexts = rowSceneTextsRef.current;
    const has = (text) => pieceSet.has(text) || isTraitSelected(prompt, text);
    rowSceneTexts.forEach((parentText, text) => {
      if (!has(text) || !has(parentText)) rowSceneTexts.delete(text);
    });
  }, [pieceSet, prompt]);

  const groups = useMemo(() => BEHAVIOR_TRAIT_GROUPS.map((group) => ({
    ...group,
    customs: normalizeCustoms(customExamples?.['행동발달']?.[group.label]),
    scenes: group.chips.flatMap((item) => scenesForChip(item.id)),
  })), [customExamples]);

  const selectedCount = useMemo(() => {
    const texts = new Set();
    groups.forEach((group) => {
      [...group.chips, ...group.customs, ...group.scenes].forEach((item) => {
        if (pieceSet.has(item.text) || isTraitSelected(prompt, item.text)) texts.add(item.text);
      });
    });
    return texts.size;
  }, [groups, pieceSet, prompt]);

  const results = useMemo(
    () => searchBehaviorTraits(groups, query, isMobile ? 40 : 16),
    [groups, query, isMobile]
  );
  const trimmedQuery = query.trim();
  if (trimmedQuery) lastSearchRef.current = { query: trimmedQuery, found: results.length };

  const sheetOpen = isMobile && sheetTab !== null;

  useEffect(() => {
    if (!sheetOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [sheetOpen]);

  const track = (stage, meta = {}) => {
    if (typeof onTrack === 'function') onTrack(stage, meta);
  };

  const flushSearchEvent = (surface) => {
    const { query: lastQuery, found } = lastSearchRef.current;
    if (lastQuery) track('search', { surface, found_count: found });
    lastSearchRef.current = { query: '', found: 0 };
  };

  const handleToggle = (item, group, surface) => {
    const wasSelected = isSelected(item.text);
    const isScene = item.type === 'scene';
    const rowSceneTexts = rowSceneTextsRef.current;
    if (wasSelected && !isScene && !item.isCustom) {
      const removing = new Map(rowSceneTexts);
      onPromptChange((prev) => removeTraitWithScenes(prev, item, removing));
      scenesForChip(item.id).forEach((scene) => rowSceneTexts.delete(scene.text));
    } else {
      onPromptChange((prev) => toggleTrait(prev, item.text));
    }
    if (isScene) {
      if (wasSelected) rowSceneTexts.delete(item.text);
      else if (surface === 'scene') rowSceneTexts.set(item.text, BEHAVIOR_TRAIT_CHIP_BY_ID[item.parentId]?.text);
      track(wasSelected ? 'remove' : 'scene_add', { surface, scope: group.key, chip_id: item.id });
      return;
    }
    if (!wasSelected) recordChipUsage(usageScope, chipUsageKey(item, group.label));
    track(wasSelected ? 'remove' : 'add', {
      surface,
      scope: group.key,
      chip_id: item.isCustom ? 'custom' : item.id,
      item_count: Math.max(0, selectedCount + (wasSelected ? -1 : 1)),
    });
    if (wasSelected) {
      if (sceneFor?.chipId === item.id) setSceneFor(null);
      return;
    }
    if (!item.isCustom && sceneDismissCount < SCENE_AUTO_OPEN_LIMIT && scenesForChip(item.id).length) {
      setSceneFor({ chipId: item.id, groupKey: group.key });
      track('scene_open', { surface, scope: group.key, chip_id: item.id });
    }
  };

  const dismissScenes = () => {
    if (!sceneFor) return;
    const next = sceneDismissCount + 1;
    setSceneDismissCount(next);
    writeSceneDismissCount(usageScope, next);
    track('scene_close', { surface: 'scene', scope: sceneFor.groupKey, chip_id: sceneFor.chipId });
    setSceneFor(null);
  };

  const insertFreeText = (surface) => {
    const text = trimmedQuery;
    if (!text) return;
    onPromptChange((prev) => (isTraitSelected(prev, text) ? prev : toggleTrait(prev, text)));
    track('add', { surface, chip_id: 'custom', item_count: selectedCount + 1 });
    setQuery('');
    setPickingSaveGroup(false);
  };

  const closeSearch = () => {
    flushSearchEvent('search');
    setSearchOpen(false);
    setQuery('');
    setPickingSaveGroup(false);
  };

  const handleOpenToggle = (group) => {
    const next = openGroup === group.key ? null : group.key;
    setOpenGroup(next);
    setAddingGroup(null);
    setNewChipText('');
    if (next) track('expand', { surface: 'inline', scope: group.key });
  };

  const openSheet = (groupKey, opener, { focusSearch = false } = {}) => {
    sheetOpenerRef.current = opener || null;
    setSheetTab(groupKey);
    setQuery('');
    setPickingSaveGroup(false);
    track('sheet_open', { surface: 'sheet', scope: focusSearch ? 'search' : groupKey });
    if (focusSearch) setTimeout(() => searchInputRef.current?.focus(), 50);
  };

  const closeSheet = () => {
    flushSearchEvent('sheet');
    setSheetTab(null);
    setQuery('');
    setAddingGroup(null);
    setPickingSaveGroup(false);
    const opener = sheetOpenerRef.current;
    if (opener && typeof opener.focus === 'function') setTimeout(() => opener.focus(), 0);
  };

  const requireLogin = () => {
    if (typeof onRequireLogin === 'function') onRequireLogin();
  };

  const startAddCustom = (group) => {
    if (isAnonymousUser) {
      requireLogin();
      return;
    }
    setAddingGroup(group.key);
    setNewChipText('');
  };

  const saveCustomText = async (group, rawText, surface) => {
    const text = String(rawText || '').trim();
    if (!text || savingCustom || typeof onSaveCustom !== 'function') return false;
    setSavingCustom(true);
    const saved = await onSaveCustom(group.label, text);
    setSavingCustom(false);
    if (saved === false) return false;
    onPromptChange((prev) => (isTraitSelected(prev, text) ? prev : toggleTrait(prev, text)));
    recordChipUsage(usageScope, chipUsageKey({ text, isCustom: true }, group.label));
    track('add', { surface, scope: group.key, chip_id: 'custom', item_count: selectedCount + 1 });
    return true;
  };

  const saveCustom = async (group) => {
    const ok = await saveCustomText(group, newChipText, 'custom_new');
    if (ok) {
      setAddingGroup(null);
      setNewChipText('');
    }
  };

  const saveSearchAsCustom = async (group) => {
    const ok = await saveCustomText(group, trimmedQuery, 'custom_search');
    if (ok) {
      setQuery('');
      setPickingSaveGroup(false);
    }
  };

  const handleSearchKeyDown = (event, surface) => {
    if (isComposingKey(event)) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      if (surface === 'sheet') closeSheet(); else closeSearch();
      return;
    }
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const first = results.find((row) => !isSelected(row.item.text));
    if (first) {
      handleToggle(first.item, first.group, surface);
      setQuery('');
    } else if (!results.length) {
      insertFreeText(surface);
    }
  };

  const renderChip = (item, group, surface) => {
    const selected = isSelected(item.text);
    const chipButton = (
      <button
        key={`${item.isCustom ? 'c' : 'l'}-${item.text}`}
        type="button"
        className={`saenggibu-trait__chip saenggibu-trait__chip--${chipTone(item)}${selected ? ' is-selected' : ''}`}
        aria-pressed={selected}
        data-role={item.type === 'scene' ? 'scene' : undefined}
        data-chip-id={item.isCustom ? 'custom' : item.id}
        onClick={() => handleToggle(item, group, surface)}
      >
        {selected && <CheckIcon />}
        {item.text}
      </button>
    );
    if (!(item.isCustom && (surface === 'expanded' || surface === 'sheet'))) return chipButton;
    return (
      <span key={`c-${item.text}`} className="saenggibu-trait__custom">
        {chipButton}
        <button
          type="button"
          data-role="tool"
          className="saenggibu-trait__custom-delete"
          aria-label={`내 칩 '${item.text}' 지우기`}
          onClick={() => onDeleteCustom?.(group.label, item.text)}
        >
          ×
        </button>
      </span>
    );
  };

  const renderSections = (group, surface) => {
    const positives = group.chips.filter((item) => item.type === 'positive');
    const negatives = group.chips.filter((item) => item.type === 'negative');
    return (
      <div className="saenggibu-trait__panel" id={`${baseId}-${group.key}-${surface}`}>
        <div className="saenggibu-trait__section">
          <span className="saenggibu-trait__section-title">잘하는 점</span>
          <div className="saenggibu-trait__chips">{positives.map((item) => renderChip(item, group, surface))}</div>
        </div>
        {negatives.length > 0 && (
          <div className="saenggibu-trait__section">
            <span className="saenggibu-trait__section-title">도와줄 점</span>
            <div className="saenggibu-trait__chips">{negatives.map((item) => renderChip(item, group, surface))}</div>
          </div>
        )}
        <div className="saenggibu-trait__section">
          <span className="saenggibu-trait__section-title">내 칩</span>
          <div className="saenggibu-trait__chips">
            {group.customs.map((item) => renderChip(item, group, surface))}
            {addingGroup !== group.key && (
              <button
                type="button"
                data-role="tool"
                className="saenggibu-trait__tool"
                onClick={() => startAddCustom(group)}
              >
                + 내 칩 만들기
              </button>
            )}
          </div>
          {addingGroup === group.key && (
            <div className="saenggibu-trait__add">
              <input
                type="text"
                className="saenggibu-trait__input"
                placeholder="예: 학급 부회장"
                aria-label={`${group.label} 내 칩 이름`}
                value={newChipText}
                maxLength={40}
                onChange={(event) => setNewChipText(event.target.value)}
                onKeyDown={(event) => {
                  if (isComposingKey(event)) return;
                  if (event.key === 'Enter') { event.preventDefault(); saveCustom(group); }
                  if (event.key === 'Escape') { setAddingGroup(null); setNewChipText(''); }
                }}
                autoFocus
              />
              <button
                type="button"
                className="saenggibu-trait__add-save"
                disabled={!newChipText.trim() || savingCustom}
                onClick={() => saveCustom(group)}
              >
                저장
              </button>
              <button
                type="button"
                className="saenggibu-trait__add-cancel"
                onClick={() => { setAddingGroup(null); setNewChipText(''); }}
              >
                취소
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderResults = (surface) => {
    if (!trimmedQuery) return null;
    if (results.length) {
      return (
        <div className="saenggibu-trait__results" data-testid="sgb-trait-search-results">
          <div className="saenggibu-trait__chips">
            {results.map((row) => renderChip(row.item, row.group, surface))}
          </div>
        </div>
      );
    }
    return (
      <div className="saenggibu-trait__results saenggibu-trait__results--empty" data-testid="sgb-trait-search-empty">
        <p className="saenggibu-trait__empty-text">‘{trimmedQuery}’ 칩은 아직 없어요</p>
        <div className="saenggibu-trait__chips">
          <button type="button" data-role="tool" className="saenggibu-trait__tool" onClick={() => insertFreeText(surface)}>
            그대로 넣기
          </button>
          {!pickingSaveGroup && (
            <button
              type="button"
              data-role="tool"
              className="saenggibu-trait__tool"
              onClick={() => (isAnonymousUser ? requireLogin() : setPickingSaveGroup(true))}
            >
              내 칩으로 저장
            </button>
          )}
        </div>
        {pickingSaveGroup && (
          <div className="saenggibu-trait__save-to">
            <span className="saenggibu-trait__section-title">어느 칸에 둘까요</span>
            <div className="saenggibu-trait__chips">
              {groups.map((group) => (
                <button
                  key={group.key}
                  type="button"
                  data-role="tool"
                  className="saenggibu-trait__tool"
                  disabled={savingCustom}
                  onClick={() => saveSearchAsCustom(group)}
                >
                  {group.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const sceneParent = sceneFor ? BEHAVIOR_TRAIT_CHIP_BY_ID[sceneFor.chipId] : null;
  const activeScene = sceneParent && isSelected(sceneParent.text) ? sceneFor : null;

  const renderSceneRow = () => {
    if (!activeScene) return null;
    const group = groups.find((item) => item.key === activeScene.groupKey);
    if (!group) return null;
    return (
      <div className="saenggibu-trait__scenes" data-testid="sgb-trait-scenes" role="group" aria-label={`${sceneParent.text} 보인 모습`}>
        <div className="saenggibu-trait__scenes-head">
          <span className="saenggibu-trait__scenes-title">‘{sceneParent.text}’ 어떤 모습이었나요?</span>
          <button
            type="button"
            data-role="tool"
            className="saenggibu-trait__scenes-close"
            aria-label="보인 모습 닫기"
            onClick={dismissScenes}
          >
            ×
          </button>
        </div>
        <div className="saenggibu-trait__chips saenggibu-trait__scenes-list">
          {scenesForChip(activeScene.chipId).map((item) => renderChip(item, group, 'scene'))}
        </div>
      </div>
    );
  };

  const renderSheet = () => {
    if (!sheetOpen) return null;
    const activeGroup = groups.find((group) => group.key === sheetTab) || groups[0];
    const sheet = (
      <div className="saenggibu-trait-sheet__layer">
        <div className="saenggibu-trait-sheet__scrim" onClick={closeSheet} aria-hidden="true" />
        <div
          className="saenggibu-trait-sheet"
          role="dialog"
          aria-modal="true"
          aria-label="학생 특성 고르기"
          data-testid="sgb-trait-sheet"
          onKeyDown={(event) => { if (event.key === 'Escape') closeSheet(); }}
        >
          <div className="saenggibu-trait-sheet__head">
            <strong className="saenggibu-trait-sheet__title">학생 특성</strong>
            <span className="saenggibu-trait-sheet__count" aria-live="polite">
              {selectedCount > 0 ? `${selectedCount}개 골랐어요` : ''}
            </span>
            <button type="button" className="saenggibu-trait-sheet__close" aria-label="닫기" onClick={closeSheet}>×</button>
          </div>
          <div className="saenggibu-trait-sheet__search">
            <SearchIcon />
            <input
              ref={searchInputRef}
              type="search"
              className="saenggibu-trait-sheet__search-input"
              data-testid="sgb-trait-sheet-search"
              placeholder="특성 찾기 (예: 조용, 집중)"
              aria-label="특성 찾기"
              value={query}
              onChange={(event) => { setQuery(event.target.value); setPickingSaveGroup(false); }}
              onKeyDown={(event) => handleSearchKeyDown(event, 'sheet')}
            />
          </div>
          {!trimmedQuery && (
            <div className="saenggibu-trait-sheet__tabs" role="tablist" aria-label="특성 묶음">
              {groups.map((group) => (
                <button
                  key={group.key}
                  type="button"
                  role="tab"
                  aria-selected={group.key === activeGroup.key}
                  className={`saenggibu-trait-sheet__tab${group.key === activeGroup.key ? ' is-active' : ''}`}
                  onClick={() => { setSheetTab(group.key); setAddingGroup(null); }}
                >
                  {group.label}
                </button>
              ))}
            </div>
          )}
          <div className="saenggibu-trait-sheet__body">
            {trimmedQuery ? renderResults('sheet') : renderSections(activeGroup, 'sheet')}
          </div>
          <div className="saenggibu-trait-sheet__foot">
            {renderSceneRow()}
            <button
              type="button"
              className="saenggibu-trait-sheet__done"
              data-testid="sgb-trait-sheet-done"
              onClick={closeSheet}
            >
              다 골랐어요
            </button>
          </div>
        </div>
      </div>
    );
    return typeof document !== 'undefined' ? createPortal(sheet, document.body) : sheet;
  };

  const slots = isMobile ? COLLAPSED_SLOTS.mobile : COLLAPSED_SLOTS.pc;

  return (
    <div
      className={`saenggibu-trait${isMobile ? ' saenggibu-trait--mobile' : ''}`}
      data-testid="sgb-trait-picker"
    >
      <div className="saenggibu-trait__head">
        {searchOpen && !isMobile ? (
          <div className="saenggibu-trait__search">
            <SearchIcon />
            <input
              ref={searchInputRef}
              type="search"
              className="saenggibu-trait__search-input"
              data-testid="sgb-trait-search-input"
              placeholder="특성 찾기 (예: 조용, 집중, 부회장)"
              aria-label="특성 찾기"
              value={query}
              onChange={(event) => { setQuery(event.target.value); setPickingSaveGroup(false); }}
              onKeyDown={(event) => handleSearchKeyDown(event, 'search')}
              autoFocus
            />
            <button type="button" data-role="tool" className="saenggibu-trait__search-close" onClick={closeSearch}>
              닫기
            </button>
          </div>
        ) : (
          <>
            <span className="saenggibu-trait__hint" aria-live="polite">
              {selectedCount > 0 ? `${selectedCount}개 골랐어요` : '누르면 아래 학생 특성에 들어가요'}
            </span>
            <button
              type="button"
              data-role="tool"
              data-testid="sgb-trait-search-open"
              className="saenggibu-trait__tool saenggibu-trait__search-open"
              onClick={(event) => (isMobile
                ? openSheet(groups[0].key, event.currentTarget, { focusSearch: true })
                : setSearchOpen(true))}
            >
              <SearchIcon />
              {isMobile ? '찾기' : '특성 찾기'}
            </button>
          </>
        )}
      </div>
      {searchOpen && !isMobile && renderResults('search')}
      {groups.map((group) => {
        const open = !isMobile && openGroup === group.key;
        const labelId = `${baseId}-${group.key}-label`;
        const { visible, extras } = buildCollapsedChips({
          group,
          customs: group.customs,
          slots: slots[group.key] || [1, 1],
          usage,
          selectedTexts: pieceSet,
        });
        const moreButton = (
          <button
            type="button"
            data-role="more"
            data-testid={`sgb-trait-more-${group.label}`}
            className={`saenggibu-trait__more${open ? ' saenggibu-trait__more--close' : ''}`}
            aria-expanded={isMobile ? undefined : open}
            aria-haspopup={isMobile ? 'dialog' : undefined}
            aria-controls={open ? `${baseId}-${group.key}-expanded` : undefined}
            aria-label={open ? `${group.label} 접기` : `${group.label} 특성 더보기`}
            onClick={(event) => (isMobile ? openSheet(group.key, event.currentTarget) : handleOpenToggle(group))}
          >
            {open ? '접기' : '더보기'}
            {!open && !isMobile && <span aria-hidden="true" className="saenggibu-trait__chevron">›</span>}
          </button>
        );
        return (
          <div
            key={group.key}
            className={`saenggibu-trait__row${open ? ' is-open' : ''}`}
            role="group"
            aria-labelledby={labelId}
          >
            <span className="saenggibu-trait__label" id={labelId}>{group.label}</span>
            <div className="saenggibu-trait__body">
              {open && renderSections(group, 'expanded')}
              {!open && (
                <div className="saenggibu-trait__chips saenggibu-trait__strip">
                  {visible.map((item) => renderChip(item, group, 'inline'))}
                  {extras.map((item) => renderChip(item, group, 'inline'))}
                  {!isMobile && moreButton}
                </div>
              )}
              {(open || isMobile) && moreButton}
              {!isMobile && activeScene?.groupKey === group.key && renderSceneRow()}
            </div>
            {isMobile && !sheetOpen && activeScene?.groupKey === group.key && renderSceneRow()}
          </div>
        );
      })}
      {renderSheet()}
    </div>
  );
};

export default BehaviorTraitPicker;
